/**
 * Local Save Service
 * Handles save data storage in IndexedDB for offline access
 */

export interface LocalSave {
  romHash: string;
  saveSlot: number;
  saveData: Uint8Array;
  timestamp: number;
  romName?: string;
}

const DB_NAME = 'DSEmulatorDB';
const DB_VERSION = 1;
const SAVES_STORE = 'saves';

export class LocalSaveService {
  private db: IDBDatabase | null = null;
  private isInitialized = false;

  /**
   * Initialize the IndexedDB database
   */
  async initialize(): Promise<void> {
    if (this.isInitialized && this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        this.isInitialized = true;
        console.log('✓ LocalSaveService initialized');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create saves object store if it doesn't exist
        if (!db.objectStoreNames.contains(SAVES_STORE)) {
          const objectStore = db.createObjectStore(SAVES_STORE, {
            keyPath: ['romHash', 'saveSlot'],
          });

          // Create indexes
          objectStore.createIndex('romHash', 'romHash', { unique: false });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  /**
   * Get a save from local storage
   */
  async getSave(romHash: string, saveSlot: number = 0): Promise<LocalSave | null> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([SAVES_STORE], 'readonly');
      const store = transaction.objectStore(SAVES_STORE);
      const request = store.get([romHash, saveSlot]);

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            romHash: result.romHash,
            saveSlot: result.saveSlot,
            saveData: new Uint8Array(result.saveData),
            timestamp: result.timestamp,
            romName: result.romName,
          });
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        reject(new Error('Failed to get save from IndexedDB'));
      };
    });
  }

  /**
   * Save data to local storage
   */
  async putSave(save: LocalSave): Promise<void> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([SAVES_STORE], 'readwrite');
      const store = transaction.objectStore(SAVES_STORE);

      const saveRecord = {
        romHash: save.romHash,
        saveSlot: save.saveSlot,
        saveData: Array.from(save.saveData), // Convert to array for storage
        timestamp: save.timestamp,
        romName: save.romName,
      };

      const request = store.put(saveRecord);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(new Error('Failed to save to IndexedDB'));
      };
    });
  }

  /**
   * Delete a save from local storage
   */
  async deleteSave(romHash: string, saveSlot: number = 0): Promise<void> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([SAVES_STORE], 'readwrite');
      const store = transaction.objectStore(SAVES_STORE);
      const request = store.delete([romHash, saveSlot]);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(new Error('Failed to delete save from IndexedDB'));
      };
    });
  }

  /**
   * Get all saves for a specific ROM
   */
  async getSavesForROM(romHash: string): Promise<LocalSave[]> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([SAVES_STORE], 'readonly');
      const store = transaction.objectStore(SAVES_STORE);
      const index = store.index('romHash');
      const request = index.getAll(romHash);

      request.onsuccess = () => {
        const results = request.result.map((result: any) => ({
          romHash: result.romHash,
          saveSlot: result.saveSlot,
          saveData: new Uint8Array(result.saveData),
          timestamp: result.timestamp,
          romName: result.romName,
        }));
        resolve(results);
      };

      request.onerror = () => {
        reject(new Error('Failed to get saves from IndexedDB'));
      };
    });
  }

  /**
   * Get all saves
   */
  async getAllSaves(): Promise<LocalSave[]> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([SAVES_STORE], 'readonly');
      const store = transaction.objectStore(SAVES_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        const results = request.result.map((result: any) => ({
          romHash: result.romHash,
          saveSlot: result.saveSlot,
          saveData: new Uint8Array(result.saveData),
          timestamp: result.timestamp,
          romName: result.romName,
        }));
        resolve(results);
      };

      request.onerror = () => {
        reject(new Error('Failed to get all saves from IndexedDB'));
      };
    });
  }

  /**
   * Clear all saves (use with caution)
   */
  async clearAllSaves(): Promise<void> {
    if (!this.db) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([SAVES_STORE], 'readwrite');
      const store = transaction.objectStore(SAVES_STORE);
      const request = store.clear();

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(new Error('Failed to clear saves from IndexedDB'));
      };
    });
  }

  /**
   * Check if database is initialized
   */
  isReady(): boolean {
    return this.isInitialized && this.db !== null;
  }

  /**
   * Close the database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.isInitialized = false;
    }
  }
}

// Singleton instance
export const localSaveService = new LocalSaveService();
